import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { getAdminListedOrgsKey, onUpdateOrgPaidStatus } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';
import { useLoadedUser } from './useUser';

const intl = getIntl('UseAdminListedOrganizations', {
    errorLoadingOrganizations: 'Error loading organizations',
    updateSuccess: 'Update successful',
    updateError: 'Error updating organization',
});

export type UseAdminListedOrganizationsArgs = {
    onUpdatedPaidStatus?: () => void;
};

export type UseAdminListedOrganizationsReturnValue = {
    adminListedOrganizations: APITypes.userService.AdminListOrganizationsResponse['organizations'];
    updatePaidStatus: (args: APITypes.userService.AdminToggleOrganizationPaidStatus) => void;
    isUpdatingPaidStatus: boolean;
};

export const useAdminListedOrganizations = (
    args: UseAdminListedOrganizationsArgs,
): UseAdminListedOrganizationsReturnValue => {
    const notify = useNotify();
    const user = useLoadedUser({});

    const adminListedOrganizationQuery = useQuery({
        queryKey: getAdminListedOrgsKey(),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.userService.AdminListOrganizationsResponse>(
                urls.api.users.adminOrganizations,
            );

            return response?.organizations;
        },
        enabled: user.user.isTestUser,
    });
    useQueryErrorNotify(adminListedOrganizationQuery, intl('errorLoadingOrganizations'));

    const updatePaidStatusMutation = useMutation({
        mutationFn: async (args: APITypes.userService.AdminToggleOrganizationPaidStatus) => {
            await appAPI.put(urls.api.users.toggleOrganizationPaidStatus, {
                body: args,
            });
        },
        onSuccess: () => {
            notify('success', intl('updateSuccess'));
            onUpdateOrgPaidStatus.resetCache({})();
            args.onUpdatedPaidStatus?.();
        },
        onError: () => {
            notify('error', intl('updateError'));
        },
    });

    return {
        adminListedOrganizations: adminListedOrganizationQuery.data || [],
        updatePaidStatus: updatePaidStatusMutation.mutate,
        isUpdatingPaidStatus: updatePaidStatusMutation.isPending,
    };
};
